import React, { FC, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import {
  DeleteWebsiteBlog,
  FindWebsiteBlogs
} from '../graphql/website-blog.graphql'
import { Button, message, Space, Table, Modal, Tag } from 'antd'
import { translate } from '../../../core'
import { InputCard } from '../../inventory/component/InputCard'
import { WebsiteBlog } from '../type/website-blog'
import { DeleteOutlined, PlusOutlined, EditOutlined, EyeOutlined, FileSearchOutlined } from '@ant-design/icons'
import { BlogPreview } from './BlogPreview'

export const BlogList: FC = () => {
  const navigate = useNavigate()
  const { loading } = useQuery(FindWebsiteBlogs, {
    notifyOnNetworkStatusChange: true,
    onCompleted: result => setData(
      (result?.websiteBlogsFind?.data ?? []).map(it => ({ ...it, key: it.id.toString() }))
    )
  })
  const [data, setData] = useState<WebsiteBlog[]>([])
  const [messageApi, contextHolder] = message.useMessage()
  const [openPreview, setOpenPreview] = useState(false)
  const [selectedId, setSelectedId] = useState<number>()

  const [deleteRow] = useMutation(DeleteWebsiteBlog, {
    refetchQueries: [FindWebsiteBlogs],
    onCompleted: () => messageApi.success(translate('blog', 'delete_success_message') || 'Blog deleted successfully'),
    onError: (err) => messageApi.error(err.message),
  })

  const handleCreate = () => {
    navigate('/admin/blogs/new')
  }

  const handleEdit = (blog: WebsiteBlog) => {
    navigate(`/admin/blogs/${blog.id}/edit`)
  }

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this blog?',
      content: 'This action cannot be undone.',
      okText: 'Yes, delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        deleteRow({
          variables: {
            id,
          }
        })
      }
    })
  }

  return (
    <InputCard contained>
      <Space>
        <Button
          type='primary'
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          {translate('add')} Blog
        </Button>
      </Space>

      <Table<WebsiteBlog>
        rowKey='key'
        className='mt-2'
        loading={loading}
        columns={[
          {
            dataIndex: 'name',
            title: 'Name',
            width: 200,
            render: (name) => <strong>{name}</strong>
          },
          {
            dataIndex: 'type',
            title: 'Type',
            width: 100,
            render: (type) => <Tag>{type}</Tag>
          },
          {
            dataIndex: 'url',
            title: 'URL',
            width: 200,
            render: (url) => <code className='text-primary'>{url}</code>
          },
          {
            dataIndex: 'id',
            key: 'actions',
            width: 120,
            render: (id, record) => (
              <Space>
                <Button
                  type='text'
                  shape='circle'
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                  title={translate('edit')}
                />
                <Button
                  type='text'
                  shape='circle'
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(id)}
                  title={translate('delete')}
                />
                <Button
                  type='text'
                  shape='circle'
                  icon={<FileSearchOutlined />}
                  onClick={() => {
                    setOpenPreview(true)
                    setSelectedId(id)
                  }}
                  title={translate('preview')}
                />
              </Space>
            )
          }
        ]}
        dataSource={data}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} ${translate('blogs')}`
        }}
      />

      <BlogPreview
        id={selectedId}
        open={openPreview}
        onClose={() => setOpenPreview(false)}
      />
      {contextHolder}
    </InputCard>
  )
}
